import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Grape, Cherry, Sprout, Leaf, Flower, Flower2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

const ICONS = { Grape, Cherry, Sprout, Leaf, Flower, Flower2 };

export function GramPlaceholder({ name, icon }) {
  const Icon = ICONS[icon] || Leaf;
  return (
    <div className="flex aspect-[3/4] w-full flex-col items-center justify-center bg-gradient-to-br from-sage-pale via-sand to-cream text-wood-500">
      <Icon size={40} className="text-sage" />
      <span className="mt-3 px-5 text-center font-serif text-lg leading-tight text-wood-700">{name}</span>
      <span className="mt-1 text-[10px] uppercase tracking-[0.25em]">Image coming soon</span>
    </div>
  );
}

export function GramCard({ product, onChoose, testid }) {
  const first = product.variants[0].grams;
  const last = product.variants[product.variants.length - 1].grams;
  return (
    <div className="flex h-full flex-col" data-testid={testid || `gram-card-${product.id}`}>
      <div className="overflow-hidden rounded-t-[38%] rounded-b-2xl bg-sand shadow-[0_6px_30px_rgba(62,49,40,0.06)]">
        {product.image
          ? <img src={product.image} alt={product.name} loading="lazy" className="aspect-[3/4] w-full object-cover" />
          : <GramPlaceholder name={product.name} icon={product.icon} />}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <h3 className="font-serif text-xl leading-snug text-wood-900">{product.name}</h3>
        {product.caffeine && <span className="rounded-full bg-wood-900 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-cream">Caffeine</span>}
      </div>
      <p className="mt-1 line-clamp-2 text-sm text-wood-500">{product.short}</p>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-base font-semibold text-wood-700">From {money(product.price_from)}</span>
        <span className="text-xs text-wood-500">{first}–{last} g</span>
      </div>
      <button onClick={() => onChoose(product)} data-testid={`choose-amount-${product.id}`} className="mt-4 w-full rounded-full bg-wood-900 py-3 text-sm font-medium text-cream transition-colors hover:bg-honey-dark">
        Choose Amount
      </button>
    </div>
  );
}

export function GramModal({ product, onClose }) {
  const { addItem } = useCart();
  const [grams, setGrams] = useState(product.variants[0].grams);
  const [added, setAdded] = useState(false);
  const variant = product.variants.find((v) => v.grams === grams);

  const add = () => {
    addItem({
      variationId: `herb:${product.id}:${grams}`,
      type: "herb",
      herbId: product.id,
      slug: product.id,
      name: product.name,
      variationName: `${grams} g • approx ${variant.oz} oz`,
      grams,
      price: variant.price,
      image: product.image || null,
    });
    setAdded(true);
    setTimeout(onClose, 700);
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-wood-900/50 md:items-center md:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="gram-modal">
        <motion.div
          className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-cream p-6 md:rounded-3xl md:p-8"
          data-lenis-prevent
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-serif text-3xl text-wood-900">{product.name}</h2>
              {product.caffeine && <span className="mt-2 inline-block rounded-full bg-wood-900 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream">Contains caffeine</span>}
            </div>
            <button onClick={onClose} aria-label="Close" data-testid="gram-modal-close" className="flex h-9 w-9 items-center justify-center rounded-full border border-wood-900/15"><X size={18} /></button>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl">
            {product.image
              ? <img src={product.image} alt={product.name} className="aspect-[4/3] w-full object-cover" />
              : <GramPlaceholder name={product.name} icon={product.icon} />}
          </div>

          <p className="mt-4 text-base leading-relaxed text-wood-700">{product.description}</p>
          <p className="mt-3 text-sm text-wood-500"><span className="font-semibold text-wood-700">Ingredient{product.ingredient.includes(" and ") ? "s" : ""}:</span> {product.ingredient}</p>
          {product.note && <p className="mt-2 rounded-xl bg-sand px-4 py-3 text-sm text-wood-700">{product.note}</p>}

          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.2em] text-wood-500">Choose your amount</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {product.variants.map((v) => (
              <button
                key={v.grams}
                onClick={() => setGrams(v.grams)}
                data-testid={`gram-variant-${product.id}-${v.grams}`}
                className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-colors ${grams === v.grams ? "border-wood-900 bg-wood-900 text-cream" : "border-wood-900/20 text-wood-900 hover:border-wood-900"}`}
              >
                <span>
                  <span className="block text-lg font-semibold">{v.grams} g</span>
                  <span className={`block text-xs ${grams === v.grams ? "text-cream/70" : "text-wood-500"}`}>approx {v.oz} oz</span>
                </span>
                <span className="font-serif text-xl">{money(v.price)}</span>
              </button>
            ))}
          </div>

          <button onClick={add} data-testid={`gram-add-${product.id}`} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-wood-900 py-4 text-lg font-medium text-cream transition-colors hover:bg-honey-dark">
            {added ? <><Check size={20} /> Added</> : <>Add {grams} g to Cart — {money(variant.price)}</>}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
